import { useCallback, useMemo } from "react"
import { GetApp } from "../App"
import { Icon, Icons } from "./Icon"
import { Menu, MenuItemDividerProps } from "./Menu"
import { Loading } from "./Loading"
import { IShip, IShipVesselType } from "../Types"
import { GetAppView, AppRoutes } from "../Views/AppView"
import { AppOverlayContent } from "../Views/AppOverlayContentView"
import { ShipRoutes } from "../Views/ShipView"

export interface MenuShipProps {
  ship?: IShip;
  // Opt-in initial open state, forwarded to the underlying `Menu`. Real callers
  // never set this; it exists so catalog fixtures can preview the item list
  // itself, not just the collapsed button (#384).
  initOpen?: boolean;
}

export const MenuShipData = {
  vesselIcon(vesselType?: IShipVesselType): string {
    return vesselType == IShipVesselType.sailing ? Icons.SailingVessel : Icons.MotorVessel;
  },
  otherShips(ships: IShip[] | undefined, currentShipId?: number): IShip[] {
    return (ships || []).filter((ship) => ship.id != currentShipId);
  },
};

export function MenuShip({ ship, initOpen }: MenuShipProps) {
  const app = GetApp();
  const t = app.getT("MenuShip");
  const { navigateApp, loadShips, setAppOverlay, ships, selectShip } = GetAppView();

  const _showNewShip = useCallback(async () => {
    if (app.cloudSession()) {
      navigateApp(AppRoutes.newShip);
    } else {
      await app.openServer("ships/new");
    }
  }, []);

  const shipId = useMemo(() => ship?.id, [ship]);

  const shipItems = useMemo(() => {
    if (!ships) {
      return [{ disabled: true, id: "ship-loading", title: <Loading /> }];
    }
    const items = MenuShipData.otherShips(ships, shipId).map((ship) => {
      return { icon: MenuShipData.vesselIcon(ship.vesselType), id: `ship-${ship.id}`, onClick: async () => {
        await selectShip(ship.id);
        return true;
      }, title: ship.name };
    });
    return items;
  }, [shipId, ship, ships]);

  const shipMenuItems = useMemo(() => {
    const shipData = { icon: MenuShipData.vesselIcon(ship?.vesselType), id: "ship_data", onClick: () => {
      if (ship) {
        navigateApp(AppRoutes.ship, { shipId: ship.id, shipRoute: ShipRoutes.edit });
      }
      return true;
    }, title: t("data_name", { name: ship?.name }) };

    const shipSettings = { icon: Icons.Settings, id: "settings", onClick: () => {
      if (ship) {
        navigateApp(AppRoutes.ship, { shipId: ship.id, shipRoute: ShipRoutes.settings });
      }
      return true;
    }, title: t("settings_name", { name: ship?.name }) };

    const shipClose = { icon: Icons.Close, id: "close", onClick: async () => {
      setAppOverlay(AppOverlayContent.closeShip);
      return true;
    }, title: t("close", { name: ship?.name }) };

    const shipLogout = { icon: Icons.Close, id: "logout", onClick: async () => {
      if (app.primarySession() && app.shipId) {
        setAppOverlay(AppOverlayContent.logout);
      } else {
        await app.logout();
      }
      return true;
    }, title: t("logout", { name: ship?.name }) };

    const shipCloud = { id: "cloud", onClick: async () => {
      if (ship) {
        await app.openServer(`ships/${ship.id}`);
      }
      return true;
    }, title: t("view_cloud", { name: ship?.name }) };

    const _shipsList = { id: "list", onClick: async () => {
      if (app.cloudSession()) {
        loadShips(true);
      }
      navigateApp(AppRoutes.accountSettings);
    }, title: t("view_ships") };

    const shipsListCloud = { id: "cloudList", onClick: async () => {
      await app.openServer("account");
      return true;
    }, title: t("view_ships") };

    if (app.cloudSession()) {
      return [
        ...shipItems,
        ...(ship && ships ? [
          MenuItemDividerProps,
          shipData,
        ] : []),
      ].filter(Boolean);
    } else {
      return [
        ...(ship ? [
          shipData,
          shipSettings,
          MenuItemDividerProps,
          ...(app.primarySession() ? [
            shipClose,
            MenuItemDividerProps,
          ] : []),
          ...(app.localSession() ? [
            shipLogout,
            MenuItemDividerProps,
          ] : []),
          shipCloud,
        ] : []),
        shipsListCloud,
      ].filter(Boolean);
    }
  }, [ship, shipItems]);

  const shipMenuButton = useMemo(() => {
    if (ship) {
      return <><Icon icon={MenuShipData.vesselIcon(ship.vesselType)} className="ship-icon" /> {ship.name} </>;
    } else {
      return <>{t("select")}</>;
    }
  }, [ship]);

  if (!ship && !app.cloudSession()) {
    return null;
  }

  return <Menu button={shipMenuButton} global={true} initOpen={initOpen} items={shipMenuItems} onClick={(selected) => { if (selected) { loadShips(); } }} />;
}
